import { getLocalDb, type LocalRitual, type RitualMode, type RitualStage } from './db';

const GUEST_USER_ID = 'guest-user';

// Re-export types for backward compatibility if needed, or update consumers.
// Ideally usage should import from db, but let's keep exports here if other files import types from here.
export type { LocalRitual, RitualMode, RitualStage };

/**
 * Create a new ritual (local storage)
 */
export async function createLocalRitual(input: {
  mode: RitualMode;
  practice_type?: 'inner-work' | 'learning';
  custom_topic?: string;
  virtue?: string;
}): Promise<LocalRitual> {
  try {
    const db = getLocalDb();
    const ritual: LocalRitual = {
      id: `ritual-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      user_id: GUEST_USER_ID,
      date: new Date().toISOString().split('T')[0],
      mode: input.mode,
      practice_type: input.practice_type,
      custom_topic: input.custom_topic,
      virtue: input.virtue,
      current_stage: 'prime',
      ended: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    await db.rituals.add(ritual);
    return ritual;
  } catch (error) {
    console.error('Failed to create local ritual:', error);
    // Return a minimal ritual object even if storage fails
    return {
      id: `ritual-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      user_id: GUEST_USER_ID,
      date: new Date().toISOString().split('T')[0],
      mode: input.mode,
      practice_type: input.practice_type,
      custom_topic: input.custom_topic,
      virtue: input.virtue,
      current_stage: 'prime',
      ended: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  }
}

/**
 * Get active ritual (local storage)
 */
export async function getActiveLocalRitual(): Promise<LocalRitual | null> {
  try {
    const db = getLocalDb();
    // Use toArray() and sort in memory since we can't chain orderBy on a where() collection easily
    // and the dataset is expected to be small for local usage.
    const rituals = await db.rituals
      .where('user_id')
      .equals(GUEST_USER_ID)
      .and((r) => !r.ended)
      .toArray();

    // Sort by created_at desc
    rituals.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    return rituals[0] || null;
  } catch (error) {
    console.error('Failed to get active local ritual:', error);
    return null;
  }
}

/**
 * Update ritual stage (local storage)
 */
export async function updateLocalRitualStage(
  ritualId: string,
  input: {
    stage: RitualStage;
    stage_data?: Record<string, unknown>;
    timers?: Record<string, number>;
  }
): Promise<LocalRitual> {
  try {
    const db = getLocalDb();
    const ritual = await db.rituals.get(ritualId);
    if (!ritual) {
      throw new Error('Ritual not found');
    }

    const updated: LocalRitual = {
      ...ritual,
      current_stage: input.stage,
      updated_at: new Date().toISOString(),
      stages: {
        ...(ritual.stages || {}),
        ...(input.stage_data ? { [input.stage]: input.stage_data } : {}),
      },
      timers: {
        ...(ritual.timers || {}),
        ...(input.timers || {}),
      },
    };

    await db.rituals.update(ritualId, updated as any);
    return updated;
  } catch (error) {
    console.error('Failed to update local ritual stage:', error);
    throw error;
  }
}

/**
 * Complete ritual (local storage)
 */
export async function completeLocalRitual(
  ritualId: string,
  input?: {
    oracle_metrics?: Record<string, unknown>;
    stages?: Record<string, unknown>;
  }
): Promise<LocalRitual> {
  try {
    const db = getLocalDb();
    const ritual = await db.rituals.get(ritualId);
    if (!ritual) {
      throw new Error('Ritual not found');
    }

    const updated: LocalRitual = {
      ...ritual,
      ended: true,
      current_stage: 'done',
      completed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      oracle_metrics: input?.oracle_metrics || ritual.oracle_metrics,
      stages: input?.stages || ritual.stages,
    };

    await db.rituals.update(ritualId, updated as any);
    return updated;
  } catch (error) {
    console.error('Failed to complete local ritual:', error);
    throw error;
  }
}

/**
 * Get current streak (local storage)
 */
export async function getLocalStreak(): Promise<number> {
  try {
    const db = getLocalDb();
    const rituals = await db.rituals
      .where('user_id')
      .equals(GUEST_USER_ID)
      .filter((r) => r.ended) // Use filter instead of .and for better strict typing with where
      .toArray();

    // Sort by completed_at desc
    rituals.sort((a, b) => {
      const dateA = a.completed_at ? new Date(a.completed_at).getTime() : 0;
      const dateB = b.completed_at ? new Date(b.completed_at).getTime() : 0;
      return dateB - dateA;
    });

    if (rituals.length === 0) return 0;

    // Calculate streak from completed rituals
    let streak = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (const ritual of rituals) {
      if (!ritual.completed_at) continue;
      const completedDate = new Date(ritual.completed_at);
      completedDate.setHours(0, 0, 0, 0);

      const daysDiff = Math.floor(
        (today.getTime() - completedDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (daysDiff === streak) {
        streak++;
      } else if (daysDiff > streak) {
        break;
      }
    }

    return streak;
  } catch (error) {
    console.error('Failed to get local streak:', error);
    return 0;
  }
}
